import React from 'react';
import styled from 'styled-components';

const Cookies = () => {
  const openPreferences = () => {
    try {
      window.dispatchEvent(new CustomEvent('sonor:open-cookie-preferences'));
    } catch (e) {
      // No-op: banner will still be available on next page load
    }
  };

  return (
    <PageContainer>
      <Section>
        <ContentWrapper>
          <PagePill>Cookies</PagePill>

          <MainHeading>Cookie Policy</MainHeading>

          <IntroText>
            This Cookie Policy explains what cookies and similar technologies
            are, how we use them on sonorai.co, and how you can manage your
            preferences. It complements our{' '}
            <a href="#privacy">Privacy Policy</a>.
          </IntroText>

          <ContentSection>
            <SectionHeading>1. What Are Cookies?</SectionHeading>
            <InfoText>
              Cookies are small text files placed on your device by websites
              you visit. They are widely used to make websites work or work
              more efficiently, to remember your preferences, and to provide
              information to the site owner. We also use similar technologies
              such as local storage, pixels, and scripts. In this policy we
              refer to all of these collectively as "cookies".
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>2. Categories of Cookies We Use</SectionHeading>
            <BulletList>
              <li>
                <strong>Strictly necessary cookies.</strong> Required for the
                website to function (for example, to remember your cookie
                preferences). These do not require consent under EU law and
                cannot be turned off through the banner.
              </li>
              <li>
                <strong>Functionality cookies.</strong> Used to remember
                choices you make and provide enhanced features (for example,
                to keep an embedded video player in the right state). Only set
                with your consent where the law requires it.
              </li>
              <li>
                <strong>Analytics cookies.</strong> Help us understand how the
                website is used in an aggregated way so we can improve it.
                Only set with your consent.
              </li>
              <li>
                <strong>Marketing / tracking cookies.</strong> Used to measure
                the effectiveness of campaigns or to deliver advertising. Only
                set with your consent.
              </li>
            </BulletList>
          </ContentSection>

          <ContentSection>
            <SectionHeading>3. Third-Party Services</SectionHeading>
            <InfoText>
              The Website embeds third-party services that may set their own
              cookies or use similar technologies when you interact with them:
            </InfoText>
            <BulletList>
              <li>
                <strong>Cal.com</strong>, used to schedule calls. When the
                booking widget loads, Cal.com may set cookies to operate the
                widget.
              </li>
              <li>
                <strong>YouTube (privacy-enhanced "nocookie" mode)</strong>,
                used to embed videos. YouTube generally does not store
                personalized cookies until you interact with the embedded
                player.
              </li>
            </BulletList>
            <InfoText>
              We may add additional providers (for example, an analytics tool)
              in the future. Where required, those providers will only be
              activated after you have consented through our cookie banner.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>4. Legal Basis</SectionHeading>
            <InfoText>
              Strictly necessary cookies are used on the basis of our
              legitimate interest in operating the Website (Art. 6(1)(f)
              GDPR). All non-essential cookies (functionality, analytics, and
              marketing) are only set after you have given consent through the
              cookie banner (Art. 6(1)(a) GDPR; Art. 122 of the Italian
              Personal Data Protection Code).
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>5. Managing Your Preferences</SectionHeading>
            <InfoText>
              You can change your preferences at any time:
            </InfoText>
            <BulletList>
              <li>Use the cookie banner shown on first visit.</li>
              <li>
                Click{' '}
                <PrefBtn type="button" onClick={openPreferences}>
                  Manage cookie preferences
                </PrefBtn>{' '}
                to reopen the banner.
              </li>
              <li>
                Most browsers also let you delete or block cookies through
                their settings. See the help pages for{' '}
                <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>,{' '}
                <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>,{' '}
                <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Firefox</a>, or{' '}
                <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Edge</a>.
              </li>
            </BulletList>
            <InfoText>
              Blocking strictly necessary cookies may prevent parts of the
              Website from working correctly.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>6. Retention</SectionHeading>
            <InfoText>
              Cookies are retained for the duration set by us or by the
              relevant third party, ranging from the end of your browser
              session to up to 12 months for analytics. You can clear cookies
              from your browser at any time.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>7. Changes</SectionHeading>
            <InfoText>
              We may update this Cookie Policy from time to time. Material
              changes will be communicated by updating this page and the "last
              updated" date below.
            </InfoText>
          </ContentSection>

          <ContentSection>
            <SectionHeading>8. Contact</SectionHeading>
            <InfoText>
              For questions about cookies, contact us at{' '}
              <a href="mailto:mohamed@sonorai.co">mohamed@sonorai.co</a>.
            </InfoText>
          </ContentSection>

          <LastUpdated>Last updated: May 2026</LastUpdated>
        </ContentWrapper>
      </Section>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: var(--bg-0);
`;

const Section = styled.section`
  padding: 120px 20px 100px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 780px;
  margin: 0 auto;
`;

const PagePill = styled.div`
  display: inline-block;
  padding: 6px 14px;
  background-color: var(--bg-2);
  border: 1px solid var(--line-2);
  border-radius: 9999px;
  color: var(--fg-2);
  font-family: var(--mono);
  font-size: 11px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.28px;
  margin-bottom: 24px;
  align-self: flex-start;
`;

const MainHeading = styled.h1`
  font-family: var(--display);
  font-size: 56px;
  font-weight: 400;
  letter-spacing: -1.6px;
  color: var(--fg-0);
  margin: 0 0 24px;
  line-height: 1.05;

  @media (max-width: 768px) {
    font-size: 40px;
    letter-spacing: -1px;
  }
`;

const IntroText = styled.p`
  font-family: var(--body);
  font-size: 17px;
  line-height: 1.55;
  color: var(--fg-2);
  margin: 0 0 48px;
  max-width: 680px;

  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const ContentSection = styled.div`
  margin-bottom: 36px;
`;

const SectionHeading = styled.h2`
  font-family: var(--body);
  font-size: 18px;
  font-weight: 600;
  color: var(--fg-0);
  margin: 0 0 12px;
  letter-spacing: -0.2px;
`;

const InfoText = styled.p`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-1);
  margin: 0 0 12px;

  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const BulletList = styled.ul`
  font-family: var(--body);
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--fg-1);
  padding-left: 22px;
  margin: 4px 0 12px;

  li { margin: 6px 0; }
  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const PrefBtn = styled.button`
  font: inherit;
  background: none;
  border: none;
  padding: 0;
  color: var(--blue);
  text-decoration: underline;
  cursor: pointer;
  &:hover { color: var(--fg-0); }
`;

const LastUpdated = styled.p`
  font-family: var(--mono);
  font-size: 11px;
  color: var(--fg-3);
  text-transform: uppercase;
  letter-spacing: 0.28px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid var(--line-2);
`;

export default Cookies;
